import { Callout } from '@fruuit/ui'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router'
import { safeNext } from '../api/client'
import { useMe } from '../api/session'
import { canRestoreOperator, exitImpersonation } from '../features/auth/api'
import { useTitle } from '../lib/useTitle'
import { AuthHeading, AuthLayout } from './AuthLayout'
import { SignInOptions } from './SignInOptions'

/** Messages for `?error=` set by the OAuth callback. All neutral (no existence oracle). */
const ERRORS: Record<string, string> = {
  oauth:
    "We couldn't sign you in with that account. If you were invited, use the email address the invitation was sent to.",
  unverified:
    "That account's email address isn't verified by the provider. Try another option or email yourself a sign-in code.",
  cancelled: 'Sign-in was cancelled. Choose an option to try again.',
  unavailable: "That sign-in option isn't available right now. Try another one.",
}

export function SignInPage() {
  useTitle('Sign in')
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const error = params.get('error')
  const me = useMe()
  const [restoring, setRestoring] = useState(false)
  const tried = useRef(false)

  // An operator's "View as" session ran out: put their own session back.
  useEffect(() => {
    if (tried.current || !me.isError || me.error.status !== 401 || !canRestoreOperator()) return
    tried.current = true
    setRestoring(true)
    void exitImpersonation()
  }, [me.isError, me.error])

  if (me.isSuccess) return <Navigate to={next ?? '/'} replace />

  return (
    <AuthLayout>
      <AuthHeading title="Sign in to Fruuit Engage">
        Access is by invitation. Use the account your invitation was sent to.
      </AuthHeading>
      {restoring ? (
        <Callout tone="info" role="status" className="mb-6">
          Your view-as session ended. Returning you to your own session…
        </Callout>
      ) : null}
      {error ? (
        <Callout tone="danger" role="alert" className="mb-6">
          {ERRORS[error] ?? ERRORS.oauth}
        </Callout>
      ) : null}
      <SignInOptions next={next} />
      <p className="mt-8 text-[13px] leading-relaxed text-sage">
        Need access? Ask your Fruuit Consulting contact to invite you.
      </p>
    </AuthLayout>
  )
}
